import HotelStatusIcon from 'components/HotelStatusIcon'
import moment from 'moment'
import { useEffect } from 'react'
import { Link } from 'react-router-dom'

import { Cell, Row } from '../../../../components/ReusableComponents/Table'
import { formatPrice } from '../../../../utils/common'
import CheckCell from '../../Common/CheckCell'
import HotelIdCell from '../../Common/HotelIdCell'
import HotelNameCell from '../../Common/HotelNameCell'
import NoteCell from '../../Common/NoteCell'
import useNote from '../../Common/useNote'
import { useNoteBatchLoader } from '../../Common/NoteBatchLoaderContext'
import styles from './PMSBErow.module.scss'

const DoubleCell = ({ primary, secondary, ...props }) => (
  <Cell className={styles.doubleCell} {...props}>
    <div className={styles.doubleCellPrimary}>{primary}</div>
    <div className={styles.doubleCellSecondary}>{secondary}</div>
  </Cell>
)

const GoToBillingAndPayments = ({ subsidiaryId }) => {
  return (
    <Link to={`/s/${subsidiaryId}/billing`}>
      <button>Go to Billing &amp; Payments →</button>
    </Link>
  )
}

const BillingRow = ({
  displayId,
  brand,
  shortName,
  isAutoPayEnabled,
  balanceCurrent,
  balanceLate,
  paymentLastReceivedOn,
  paymentLastAmount,
  emailLastSentOn,
  subsidiaryId,
  index,
  subsidiaryStatus,
  rowStatus,
  statusExplanation,
  csmUserFullName,
  paymentMethod,
  paymentPeriod,
  packagePlans,
  packageFee,
  discount,
  monthlyFeeAfterDiscount,
  serviceStartDate,
  addedOn,
  serviceBlockOn
}) => {
  const { registerCallback, notesCache } = useNoteBatchLoader()
  const {
    note,
    setNote,
    saveImmediately,
    isLoading: isLoadingNote,
    isSaving,
    error: noteError,
    loadNoteOnDemand,
    setNoteFromBatch
  } = useNote(subsidiaryId, 'healthCheck-billing', { batchLoad: true })

  // Register callback to receive batch-loaded notes
  useEffect(() => {
    const key = `${subsidiaryId}-healthCheck-billing`
    
    // Check if note is already in cache
    if (notesCache.has(key)) {
      setNoteFromBatch(notesCache.get(key))
    }

    // Register callback for future updates
    const unregister = registerCallback(subsidiaryId, 'healthCheck-billing', (data) => {
      setNoteFromBatch(data)
    })

    return unregister
  }, [subsidiaryId, registerCallback, notesCache, setNoteFromBatch])

  return (
    <div
      className={`${styles.rowHolder} ${rowStatus ? styles[rowStatus] : ''}`}
      title={statusExplanation}
    >
      <div className={styles.statusIcon}>
        <HotelStatusIcon status={subsidiaryStatus} />
      </div>
      <div className={styles.index}>{index}</div>
      <Row>
        <HotelIdCell displayId={displayId} />
        <HotelNameCell
          brand={brand}
          name={shortName}
          navigationControl={<GoToBillingAndPayments subsidiaryId={subsidiaryId} />}
          paymentMethod={paymentMethod}
          paymentPeriod={paymentPeriod}
          packagePlans={packagePlans}
          packageFee={packageFee}
          discount={discount}
          monthlyFeeAfterDiscount={monthlyFeeAfterDiscount}
          serviceStartDate={serviceStartDate}
          addedOn={addedOn}
          serviceBlockOn={serviceBlockOn}
          emailLastSentOn={emailLastSentOn}
        />
        <Cell className={styles.textCell}>
          <span title={csmUserFullName}>
            {csmUserFullName
              ?.split(' ')
              .map(str => str[0])
              .join('.')}
          </span>
        </Cell>
        <CheckCell isChecked={isAutoPayEnabled} />
        <Cell className={styles.textCell}>{formatPrice(balanceCurrent)}</Cell>
        <Cell className={styles.textCell}>{formatPrice(balanceLate)}</Cell>
        <Cell className={styles.textCell}>
          {serviceBlockOn ? (
            <div className={styles.serviceBlockWarning}>
              <span className={styles.serviceBlockDate}>
                {moment(serviceBlockOn).format('MMM DD, YYYY')}
              </span>
              <span className={styles.serviceBlockDays}>
                ({moment(serviceBlockOn).diff(moment(), 'days')} days)
              </span>
            </div>
          ) : (
            <span>-</span>
          )}
        </Cell>
        <DoubleCell primary={formatPrice(paymentLastAmount)} />
        <Cell className={styles.textCell}>
          {moment(paymentLastReceivedOn).format('MMM DD, YYYY')}
        </Cell>
        <NoteCell
          note={note}
          setNote={setNote}
          saveImmediately={saveImmediately}
          loadNoteOnDemand={loadNoteOnDemand}
          isLoading={isLoadingNote}
          isSaving={isSaving}
          error={noteError}
        />
      </Row>
    </div>
  )
}

export default BillingRow
